import { MultiSelect, Select } from "@components";
import { CheckboxGroup, type Selection } from "react-aria-components";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import {
	RadioButton,
	RadioGroup,
} from "@/components/base/radio-buttons/radio-buttons";
import { QuestionOptionDisplayType } from "@/types/question";
import { cx } from "@/utils/cx";

export type OptionDisplayOption = {
	id: string | number;
	label: string;
};

export type OptionDisplayFieldProps = {
	optionDisplayType: number;
	isMultipleSelection?: boolean;
	options: OptionDisplayOption[];
	/** Interactive for checklist; readonly for previews/modals */
	isDisabled?: boolean;
	isInvalid?: boolean;
	ariaLabel?: string;
	/** Allow clearing single selection (non-required questions) */
	allowDeselect?: boolean;
	/**
	 * Single: string | number | null | undefined
	 * Multi (checkbox/list multi): string[]
	 */
	value?: string | number | string[] | null;
	onChange?: (value: string | string[] | undefined) => void;
	className?: string;
};

function toOptionId(id: string | number): string {
	return String(id);
}

function normalizeOptions(options: OptionDisplayOption[]) {
	return options.map((op) => ({
		id: toOptionId(op.id),
		label: op.label,
	}));
}

function selectionToStringArray(keys: Selection): string[] {
	if (keys === "all") {
		return [];
	}
	return Array.from(keys).map(String);
}

function toSingleOptionValue(
	value: string | number | string[] | null | undefined
): string | undefined {
	if (Array.isArray(value)) {
		return value[0];
	}
	if (value === null || value === undefined || value === "") {
		return;
	}
	return String(value);
}

function toMultiOptionValue(
	value: string | number | string[] | null | undefined
): string[] {
	if (Array.isArray(value)) {
		return value.map(String);
	}
	if (value === null || value === undefined || value === "") {
		return [];
	}
	return [String(value)];
}

function CheckboxOptions({
	options,
	isMultipleSelection,
	isDisabled,
	ariaLabel,
	allowDeselect,
	value,
	onChange,
	className,
}: {
	options: { id: string; label: string }[];
	isMultipleSelection: boolean;
	isDisabled?: boolean;
	ariaLabel?: string;
	allowDeselect?: boolean;
	value?: string | number | string[] | null;
	onChange?: (value: string | string[] | undefined) => void;
	className?: string;
}) {
	const selectedKeys: string[] = (() => {
		if (Array.isArray(value)) {
			return value.map(String);
		}
		if (value !== null && value !== undefined && value !== "") {
			return [String(value)];
		}
		return [];
	})();

	return (
		<CheckboxGroup
			aria-label={ariaLabel}
			className={cx("flex flex-col gap-2", className)}
			isDisabled={isDisabled}
			onChange={(keys) => {
				if (isDisabled) {
					return;
				}
				const next = keys.map(String);
				if (isMultipleSelection) {
					onChange?.(next);
					return;
				}
				const prev = selectedKeys;
				const added = next.find((key) => !prev.includes(key));
				if (added) {
					onChange?.(added);
					return;
				}
				if (next.length === 0 && allowDeselect) {
					onChange?.(undefined);
					return;
				}
				if (next.length === 1) {
					onChange?.(next[0]);
				}
			}}
			value={selectedKeys}
		>
			{options.map((op) => (
				<Checkbox
					isDisabled={isDisabled}
					key={op.id}
					label={op.label}
					value={op.id}
				>
					{op.label}
				</Checkbox>
			))}
		</CheckboxGroup>
	);
}

function RadioOptions({
	options,
	isDisabled,
	ariaLabel,
	allowDeselect,
	value,
	onChange,
	className,
}: {
	options: { id: string; label: string }[];
	isDisabled?: boolean;
	ariaLabel?: string;
	allowDeselect?: boolean;
	value?: string | number | null;
	onChange?: (value: string | undefined) => void;
	className?: string;
}) {
	const selected = value !== null && value !== undefined ? String(value) : "";

	return (
		<RadioGroup
			aria-label={ariaLabel}
			className={cx("flex flex-col gap-2", className)}
			isDisabled={isDisabled}
			onChange={(next) => {
				if (isDisabled) {
					return;
				}
				if (allowDeselect === true && next === selected) {
					onChange?.(undefined);
					return;
				}
				onChange?.(next);
			}}
			value={selected}
		>
			{options.map((op) => (
				<RadioButton
					key={op.id}
					label={op.label}
					onClick={() => {
						if (isDisabled) {
							return;
						}
						if (allowDeselect === true && selected === op.id) {
							onChange?.(undefined);
						}
					}}
					value={op.id}
				>
					{op.label}
				</RadioButton>
			))}
		</RadioGroup>
	);
}

function ListMultiOptions({
	options,
	isDisabled,
	isInvalid,
	ariaLabel,
	value,
	onChange,
	className,
}: {
	options: { id: string; label: string }[];
	isDisabled?: boolean;
	isInvalid?: boolean;
	ariaLabel?: string;
	value?: string[] | null;
	onChange?: (value: string[]) => void;
	className?: string;
}) {
	const selectedKeys = new Set((value ?? []).map(String));

	return (
		<MultiSelect
			aria-label={ariaLabel}
			className={cx("w-full", className)}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			items={options}
			onSelectionChange={(keys: Selection) => {
				if (isDisabled) {
					return;
				}
				onChange?.(selectionToStringArray(keys));
			}}
			placeholder="Selecione..."
			selectedKeys={selectedKeys}
			showFooter={false}
			showSearch={false}
		>
			{(item) => (
				<MultiSelect.Item
					id={item.id}
					selectionIndicator="none"
					textValue={item.label}
				>
					{item.label}
				</MultiSelect.Item>
			)}
		</MultiSelect>
	);
}

function ComboBoxOptions({
	options,
	isDisabled,
	isInvalid,
	ariaLabel,
	allowDeselect,
	value,
	onChange,
	className,
}: {
	options: { id: string; label: string }[];
	isDisabled?: boolean;
	isInvalid?: boolean;
	ariaLabel?: string;
	allowDeselect?: boolean;
	value?: string | number | null;
	onChange?: (value: string | undefined) => void;
	className?: string;
}) {
	const selectedKey =
		value !== null && value !== undefined && value !== ""
			? String(value)
			: null;

	return (
		<Select
			aria-label={ariaLabel}
			className={cx("w-full", className)}
			isClearable={allowDeselect}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			items={options}
			onChange={(key) => {
				if (isDisabled) {
					return;
				}
				if (key === null || key === undefined || key === "") {
					onChange?.(undefined);
					return;
				}
				onChange?.(String(key));
			}}
			placeholder="Selecione..."
			value={selectedKey}
		>
			{(item) => (
				<Select.Item id={item.id} label={item.label}>
					{item.label}
				</Select.Item>
			)}
		</Select>
	);
}

/**
 * Shared option display for checklist execution and form/question previews.
 * Visual stays stable per optionDisplayType; isMultipleSelection only changes selection rules
 * (except List multi → MultiSelect).
 */
export function OptionDisplayField({
	optionDisplayType,
	isMultipleSelection = false,
	options,
	isDisabled = false,
	isInvalid = false,
	ariaLabel,
	allowDeselect = false,
	value,
	onChange,
	className,
}: OptionDisplayFieldProps) {
	const normalized = normalizeOptions(options);

	if (optionDisplayType === QuestionOptionDisplayType.Checkbox) {
		return (
			<CheckboxOptions
				allowDeselect={allowDeselect}
				ariaLabel={ariaLabel}
				className={className}
				isDisabled={isDisabled}
				isMultipleSelection={isMultipleSelection}
				onChange={onChange}
				options={normalized}
				value={value}
			/>
		);
	}

	if (optionDisplayType === QuestionOptionDisplayType.Radio) {
		return (
			<RadioOptions
				allowDeselect={allowDeselect}
				ariaLabel={ariaLabel}
				className={className}
				isDisabled={isDisabled}
				onChange={onChange}
				options={normalized}
				value={toSingleOptionValue(value)}
			/>
		);
	}

	if (optionDisplayType === QuestionOptionDisplayType.ComboBox) {
		return (
			<ComboBoxOptions
				allowDeselect={allowDeselect}
				ariaLabel={ariaLabel}
				className={className}
				isDisabled={isDisabled}
				isInvalid={isInvalid}
				onChange={onChange}
				options={normalized}
				value={toSingleOptionValue(value)}
			/>
		);
	}

	if (optionDisplayType === QuestionOptionDisplayType.List) {
		if (isMultipleSelection) {
			return (
				<ListMultiOptions
					ariaLabel={ariaLabel}
					className={className}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					onChange={(next) => onChange?.(next)}
					options={normalized}
					value={toMultiOptionValue(value)}
				/>
			);
		}

		return (
			<RadioOptions
				allowDeselect={allowDeselect}
				ariaLabel={ariaLabel}
				className={className}
				isDisabled={isDisabled}
				onChange={onChange}
				options={normalized}
				value={toSingleOptionValue(value)}
			/>
		);
	}

	return null;
}
